// Shared state for the Festive Greetings / Promotional pages.
//
// The filters (date, name search, firm, mobile) need to live in the
// HEADER ROW (yellow bar) but they affect content rendered inside
// PromotionalPage which is a sibling of Header in the layout tree. The
// cleanest way to share that state is a context provider mounted high
// up in the tree (inside MainLayout, around both Header and Outlet).
//
// The provider also owns the firm-loading + dealer-detection logic so
// PromotionalPage and Header read the same firm list from the same
// source.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SHEETS, forceRefreshSheet } from "../utils/api";
import { usePolling } from "../utils/usePolling";

const FIRMS_CACHE_KEY = "promotional_firms_cache_v2";

const last10 = (v) =>
  String(v ?? "")
    .replace(/\D/g, "")
    .slice(-10);

const CreativesFiltersContext = createContext(null);

export function CreativesFiltersProvider({ children }) {
  const [dealer, setDealer] = useState(null);
  const [dateFilter, setDateFilter] = useState(""); // YYYY-MM-DD
  const [nameFilter, setNameFilter] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);

  // Lazy-init firms from localStorage so the dropdown is populated INSTANTLY
  // on every page load — no blank flicker while polling fetches the latest.
  const [selectedFirm, setSelectedFirm] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(FIRMS_CACHE_KEY) || "null");
      return c?.selected || "";
    } catch {
      return "";
    }
  });
  const [firms, setFirms] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem(FIRMS_CACHE_KEY) || "null");
      return Array.isArray(c?.list) ? c.list : [];
    } catch {
      return [];
    }
  });
  const [mobileInput, setMobileInput] = useState("");
  // Free-form text the dealer can type to OVERRIDE the default "FIRM-MOBILE"
  // footer on downloads (e.g. their tagline, a personalized greeting, etc.).
  // Empty string = use the default firm-mobile composition.
  const [customFooterText, setCustomFooterText] = useState("");
  // PromotionalPage pushes the list of YYYY-MM-DD keys it has creatives
  // for; the header's date picker uses this to yellow-highlight available
  // dates so the user can immediately see which days have content.
  const [availableDates, setAvailableDates] = useState([]);

  // Load dealer once
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("dealer") || "null");
      setDealer(stored);
    } catch {
      setDealer(null);
    }
  }, []);

  const dealerMobile = useMemo(
    () =>
      last10(
        dealer?.phone ||
          dealer?.["Contact No"] ||
          dealer?.["contact_no"] ||
          dealer?.Mobile ||
          ""
      ),
    [dealer]
  );

  // Initialize editable mobile from logged-in dealer's phone
  useEffect(() => {
    if (dealerMobile && !mobileInput) {
      setMobileInput(dealerMobile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerMobile]);

  // Persist firm selection
  useEffect(() => {
    if (!selectedFirm) return;
    try {
      const c = JSON.parse(localStorage.getItem(FIRMS_CACHE_KEY) || "null") || {};
      localStorage.setItem(
        FIRMS_CACHE_KEY,
        JSON.stringify({ ...c, selected: selectedFirm })
      );
    } catch {}
  }, [selectedFirm]);

  // Fetch dealer's firms — preserves user's current selection on refresh.
  const fetchFirms = useCallback(async () => {
    if (!dealerMobile) return;
    try {
      // FIRE-AND-FORGET — don't await. The backend refresh endpoint hits
      // Apps Script (slow), so awaiting here would block context init on
      // every page navigation that mounts a creatives view. The 60s
      // polling will pick up any newly-added firm soon enough, and the
      // backend already auto-syncs dealers every 2 min.
      forceRefreshSheet("dealers").catch(() => {});
      const res = await fetch(SHEETS.dealers(dealerMobile), { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json().catch(() => []);
      const list = (Array.isArray(data) ? data : [])
        .map((r) => {
          const shortName = String(
            r?.["Short Name"] ||
              r?.["Short name"] ||
              r?.["short name"] ||
              r?.["SHORT NAME"] ||
              r?.ShortName ||
              r?.shortName ||
              r?.short_name ||
              r?.Short ||
              ""
          ).trim();
          if (shortName) return shortName;
          return String(
            r?.Particulars ||
              r?.particulars ||
              r?.["Company Name"] ||
              r?.["Firm Name"] ||
              r?.firmName ||
              r?.firm ||
              r?.Name ||
              ""
          ).trim();
        })
        .filter(Boolean);
      const uniq = [...new Set(list)];

      let final = uniq;
      if (final.length === 0) {
        const fromDealer = String(
          dealer?.["Short Name"] ||
            dealer?.shortName ||
            dealer?.firmName ||
            dealer?.["Company Name"] ||
            dealer?.Particulars ||
            ""
        ).trim();
        if (fromDealer) final = [fromDealer];
      }

      setFirms((prevFirms) => {
        if (final.length === 0 && prevFirms.length > 0) return prevFirms;
        const same =
          prevFirms.length === final.length &&
          prevFirms.every((f, i) => f === final[i]);
        return same ? prevFirms : final;
      });
      setSelectedFirm((prev) => {
        if (prev && final.includes(prev)) return prev;
        if (final.length === 0) return prev;
        return final[0] || "";
      });

      if (final.length > 0) {
        try {
          localStorage.setItem(
            FIRMS_CACHE_KEY,
            JSON.stringify({ list: final, selected: final[0] })
          );
        } catch {}
      }
    } catch {
      /* swallow */
    }
  }, [dealerMobile, dealer]);

  useEffect(() => {
    fetchFirms();
  }, [fetchFirms]);

  // Auto-poll firms every 60s — admin add/remove firms reflect quickly.
  usePolling(fetchFirms, 60_000);

  const value = useMemo(
    () => ({
      dealer,
      dealerMobile,
      dateFilter,
      setDateFilter,
      nameFilter,
      setNameFilter,
      showCalendar,
      setShowCalendar,
      selectedFirm,
      setSelectedFirm,
      firms,
      mobileInput,
      setMobileInput,
      customFooterText,
      setCustomFooterText,
      availableDates,
      setAvailableDates,
    }),
    [
      dealer,
      dealerMobile,
      dateFilter,
      nameFilter,
      showCalendar,
      selectedFirm,
      firms,
      mobileInput,
      customFooterText,
      availableDates,
    ]
  );

  return (
    <CreativesFiltersContext.Provider value={value}>
      {children}
    </CreativesFiltersContext.Provider>
  );
}

export function useCreativesFilters() {
  const ctx = useContext(CreativesFiltersContext);
  if (!ctx) {
    // Allow Header to call this even outside provider (returns nulls so
    // the conditional render falls through). PromotionalPage will be
    // inside the provider so it gets real values.
    return null;
  }
  return ctx;
}
